import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { marketingAudienciasAPI } from '../../services/api';
import { Loader2, Users, Calculator, Eye, Archive, Plus, ShieldOff, History } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// F14D — Audiencias territoriales deterministas: reglas, estimación fechada y vista previa
// redactada. Sin IA generativa, sin envíos y sin exportación de PII.
const REGLA_VACIA = { campo: '', operador: 'igual', valor: '' };

export const PanelAudienciasMarketing = ({ seccion }) => {
  const { toast } = useToast();
  const [catalogos, setCatalogos] = useState(null);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nueva, setNueva] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [historial, setHistorial] = useState(null);

  const cargar = () => {
    setCargando(true);
    marketingAudienciasAPI.listar({ seccion_id: seccion || undefined })
      .then((d) => setItems(d.items || [])).catch(() => setItems([]))
      .finally(() => setCargando(false));
  };

  useEffect(() => { marketingAudienciasAPI.catalogos().then(setCatalogos).catch(() => {}); }, []);
  useEffect(cargar, [seccion]); // eslint-disable-line

  const campos = nueva && catalogos ? (catalogos.campos_por_fuente[nueva.fuente] || []) : [];

  const abrirNueva = () => setNueva({
    nombre: '', fuente: 'afiliados', base_legal: 'relacion_contractual', canal: 'email',
    combinacion: 'AND', descripcion: '', seccion_id: seccion || '',
    reglas: [{ ...REGLA_VACIA }],
  });

  const guardar = async () => {
    try {
      const cuerpo = {
        ...nueva,
        seccion_id: nueva.seccion_id || undefined,
        reglas: nueva.reglas.filter((r) => r.campo).map((r) => ({
          campo: r.campo, operador: r.operador,
          valor: ['en', 'no_en', 'entre'].includes(r.operador)
            ? String(r.valor).split(',').map((v) => v.trim()).filter(Boolean)
            : (r.operador === 'existe' ? r.valor === 'true' : r.valor),
        })),
      };
      await marketingAudienciasAPI.crear(cuerpo);
      toast({ title: 'Audiencia creada', description: 'Reglas versión 1 registradas' });
      setNueva(null); cargar();
    } catch (e) {
      toast({ title: 'No se pudo crear', description: e.message, variant: 'destructive' });
    }
  };

  const estimar = async (id) => {
    try {
      const d = await marketingAudienciasAPI.estimar(id);
      setDetalle(d); cargar();
      toast({ title: `Estimación: ${d.estimacion.coincidencias_elegibles} elegible(s)` });
    } catch (e) {
      toast({ title: 'No se pudo estimar', description: e.message, variant: 'destructive' });
    }
  };

  const verPrevia = async (id) => {
    try { setPrevia(await marketingAudienciasAPI.vistaPrevia(id, 10)); }
    catch (e) { toast({ title: 'Vista previa rechazada', description: e.message,
      variant: 'destructive' }); }
  };

  const verHistorial = async (id) => {
    try { setHistorial(await marketingAudienciasAPI.historial(id)); }
    catch (e) { toast({ title: 'No se pudo abrir el historial', variant: 'destructive' }); }
  };

  const archivar = async (id) => {
    try { await marketingAudienciasAPI.archivar(id); toast({ title: 'Audiencia archivada' });
      cargar(); }
    catch (e) { toast({ title: 'No se pudo archivar', description: e.message,
      variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="panel-audiencias-marketing">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Users className="h-4 w-4 text-indigo-600" /> Audiencias territoriales
        </span>
        <Badge className="bg-slate-100 text-slate-700">Sin envíos · PII redactada</Badge>
        <Button size="sm" onClick={abrirNueva} className="ml-auto"
                data-testid="audiencia-nueva-btn">
          <Plus className="h-4 w-4 mr-1" /> Nueva audiencia
        </Button>
      </div>

      {cargando && (
        <div className="flex items-center text-gray-500 py-6" data-testid="audiencias-cargando">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cargando audiencias…
        </div>
      )}

      {!cargando && items.length === 0 && (
        <Card><CardContent className="p-6 text-sm text-gray-500"
                           data-testid="audiencias-vacio">
          Aún no hay audiencias en tu alcance. Crea la primera con reglas deterministas
          (por ejemplo, afiliados por renovar).
        </CardContent></Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((a) => (
          <Card key={a.id} data-testid={`audiencia-card-${a.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span>{a.nombre}</span>
                <Badge className={a.estado === 'activa' ? 'bg-emerald-100 text-emerald-800'
                                                        : 'bg-gray-200 text-gray-700'}>
                  {a.estado}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-gray-500">
                {a.fuente} · {a.canal} · {a.base_legal} · v{a.version_reglas}
                {a.seccion ? ` · ${a.seccion}` : ''}
              </p>
              <p className="text-gray-700" data-testid={`audiencia-filtros-${a.id}`}>
                {a.explicacion_filtros?.[0] || 'Sin reglas'}
              </p>
              <p className="text-gray-900 font-medium" data-testid={`audiencia-tamano-${a.id}`}>
                {a.tamano_estimado == null
                  ? 'Sin estimar'
                  : `${a.tamano_estimado} elegible(s)`}
                {a.calculado_en && (
                  <span className="text-xs font-normal text-gray-500">
                    {' '}· calculado {new Date(a.calculado_en).toLocaleString('es-MX')}
                    {a.estimacion_caducada ? ' (caducada)' : ''}
                  </span>
                )}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => estimar(a.id)}
                        data-testid={`audiencia-estimar-${a.id}`}>
                  <Calculator className="h-4 w-4 mr-1" /> Estimar
                </Button>
                <Button size="sm" variant="outline" onClick={() => verPrevia(a.id)}
                        data-testid={`audiencia-previa-${a.id}`}>
                  <Eye className="h-4 w-4 mr-1" /> Vista previa
                </Button>
                <Button size="sm" variant="outline" onClick={() => verHistorial(a.id)}
                        data-testid={`audiencia-historial-${a.id}`}>
                  <History className="h-4 w-4 mr-1" /> Historial
                </Button>
                {a.estado === 'activa' && (
                  <Button size="sm" variant="outline" onClick={() => archivar(a.id)}
                          data-testid={`audiencia-archivar-${a.id}`}>
                    <Archive className="h-4 w-4 mr-1" /> Archivar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {nueva && catalogos && (
        <Card data-testid="audiencia-form">
          <CardHeader><CardTitle className="text-base">Nueva audiencia territorial</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Nombre"
                     value={nueva.nombre} data-testid="audiencia-nombre"
                     onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })} />
              <select className="border rounded-lg px-3 py-2 text-sm bg-white"
                      value={nueva.fuente} data-testid="audiencia-fuente"
                      onChange={(e) => setNueva({ ...nueva, fuente: e.target.value,
                        reglas: [{ ...REGLA_VACIA }] })}>
                {catalogos.fuentes.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select className="border rounded-lg px-3 py-2 text-sm bg-white"
                      value={nueva.base_legal} data-testid="audiencia-base-legal"
                      onChange={(e) => setNueva({ ...nueva, base_legal: e.target.value })}>
                {catalogos.bases_legales.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select className="border rounded-lg px-3 py-2 text-sm bg-white"
                      value={nueva.combinacion} data-testid="audiencia-combinacion"
                      onChange={(e) => setNueva({ ...nueva, combinacion: e.target.value })}>
                <option value="AND">Cumple TODAS las reglas (AND)</option>
                <option value="OR">Cumple ALGUNA regla (OR)</option>
              </select>
            </div>

            {nueva.reglas.map((r, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-4">
                <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={r.campo}
                        data-testid={`audiencia-regla-campo-${i}`}
                        onChange={(e) => {
                          const reglas = [...nueva.reglas];
                          reglas[i] = { ...r, campo: e.target.value };
                          setNueva({ ...nueva, reglas });
                        }}>
                  <option value="">Campo…</option>
                  {campos.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
                <select className="border rounded-lg px-3 py-2 text-sm bg-white"
                        value={r.operador} data-testid={`audiencia-regla-operador-${i}`}
                        onChange={(e) => {
                          const reglas = [...nueva.reglas];
                          reglas[i] = { ...r, operador: e.target.value };
                          setNueva({ ...nueva, reglas });
                        }}>
                  {catalogos.operadores.map((o) => (
                    <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>))}
                </select>
                <input className="border rounded-lg px-3 py-2 text-sm md:col-span-2"
                       placeholder="Valor (para «en»/«entre», separa con comas)"
                       value={r.valor} data-testid={`audiencia-regla-valor-${i}`}
                       onChange={(e) => {
                         const reglas = [...nueva.reglas];
                         reglas[i] = { ...r, valor: e.target.value };
                         setNueva({ ...nueva, reglas });
                       }} />
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="audiencia-agregar-regla"
                      onClick={() => setNueva({ ...nueva,
                        reglas: [...nueva.reglas, { ...REGLA_VACIA }] })}>
                <Plus className="h-4 w-4 mr-1" /> Regla
              </Button>
              <Button size="sm" onClick={guardar} data-testid="audiencia-guardar">Guardar</Button>
              <Button size="sm" variant="ghost" onClick={() => setNueva(null)}
                      data-testid="audiencia-cancelar">Cancelar</Button>
              <span className="text-xs text-gray-400 self-center">
                La sección se toma de tu alcance territorial y no se puede cambiar aquí.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {detalle && (
        <Card data-testid="audiencia-estimacion">
          <CardHeader><CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" /> Estimación · {detalle.audiencia.nombre}
          </CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Candidatos en alcance: <b>{detalle.estimacion.candidatos_en_alcance}</b></p>
            <p>Elegibles: <b>{detalle.estimacion.coincidencias_elegibles}</b></p>
            <p className="text-gray-600">
              Excluidos — sin correo: {detalle.estimacion.excluidos.sin_correo} ·
              sin base legal: {detalle.estimacion.excluidos.sin_base_legal} ·
              en supresión: {detalle.estimacion.excluidos.en_supresion}
            </p>
            <p className="text-xs text-gray-500">
              Filtros: {detalle.estimacion.explicacion_filtros?.[0] || '—'} · calculado{' '}
              {new Date(detalle.estimacion.calculado_en).toLocaleString('es-MX')}
            </p>
            <Button size="sm" variant="ghost" onClick={() => setDetalle(null)}
                    data-testid="audiencia-estimacion-cerrar">Cerrar</Button>
          </CardContent>
        </Card>
      )}

      {previa && (
        <Card data-testid="audiencia-vista-previa">
          <CardHeader><CardTitle className="text-base flex items-center gap-2">
            <ShieldOff className="h-4 w-4" /> Vista previa redactada
            <Badge className="bg-amber-100 text-amber-800">No exportable</Badge>
          </CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="text-gray-500">
              {previa.mostrados} de {previa.total_elegibles} · sólo iniciales y correo enmascarado
            </p>
            {previa.miembros.map((m) => (
              <p key={m.id} data-testid={`audiencia-miembro-${m.id}`}>
                {m.nombre} · {m.correo} · {m.etiqueta || '—'} · consentimiento: {m.consentimiento}
              </p>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setPrevia(null)}
                    data-testid="audiencia-previa-cerrar">Cerrar</Button>
          </CardContent>
        </Card>
      )}

      {historial && (
        <Card data-testid="audiencia-historial">
          <CardHeader><CardTitle className="text-base">
            Historial de reglas (append-only) · versión vigente {historial.version_vigente}
          </CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {historial.versiones.map((v) => (
              <div key={v.version} data-testid={`audiencia-version-${v.version}`}>
                <p className="font-medium">
                  v{v.version} {v.vigente && <Badge className="bg-emerald-100 text-emerald-800">
                    vigente</Badge>}
                  <span className="text-xs font-normal text-gray-500">
                    {' '}· {v.creada_en ? new Date(v.creada_en).toLocaleString('es-MX') : '—'}
                  </span>
                </p>
                <p className="text-gray-600 text-xs">
                  {v.reglas.map((r) => `${r.campo} ${r.operador} ${
                    Array.isArray(r.valor) ? r.valor.join('/') : r.valor}`).join(' · ')}
                </p>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setHistorial(null)}
                    data-testid="audiencia-historial-cerrar">Cerrar</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PanelAudienciasMarketing;
